"use client";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchSubscriptionHistory } from "../../../services/billingService";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { formatPaise, formatBillingDate, getSubscriptionGraceInfo } from "../../../utils/billingFormat";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SchoolSubscriptionHistory = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchSubscriptionHistory(schoolId)
      .then((res) => setSubscriptions(res.data || []))
      .catch((err) => setError(extractApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [schoolId]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900">Subscription History</h1>
        <Button variant="outline" onClick={() => navigate(`/dashboard/schools/${schoolId}/billing`)}>
          Back to Billing
        </Button>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm">{error}</div>}

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
        ) : subscriptions.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">No subscriptions yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Max Users</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((sub, idx) => {
                // If it's the latest subscription (idx === 0), calculate real-time grace info
                const graceInfo = idx === 0 ? getSubscriptionGraceInfo(sub) : null;
                return (
                  <TableRow key={sub._id}>
                    <TableCell>{sub.type === "TRIAL" ? "Trial Access" : (sub.plan?.name || "-")}</TableCell>
                    <TableCell>{sub.type === "TRIAL" ? "₹0 (Trial)" : formatPaise(sub.totalAmount)}</TableCell>
                    <TableCell>{sub.maxUsers}</TableCell>
                    <TableCell>{formatBillingDate(sub.startDate)} → {formatBillingDate(sub.endDate)}</TableCell>
                    <TableCell>
                      {graceInfo ? (
                        <Badge className={graceInfo.badgeClass} variant={graceInfo.variant}>
                          {graceInfo.label}
                        </Badge>
                      ) : sub.type === "TRIAL" ? (
                        <Badge className="bg-blue-600 hover:bg-blue-600 text-white">Trial</Badge>
                      ) : (
                        <Badge variant="secondary">{sub.status}</Badge>
                      )}
                    </TableCell>
                    <TableCell>{sub.source}</TableCell>
                    <TableCell>{formatBillingDate(sub.createdAt)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default SchoolSubscriptionHistory;
